<template>
  <div
    class="flex flex-col right-[13rem] -translate-y-[1rem] md:-translate-y-[33rem] rounded-[28px] bg-GRIS_CLARO px-6 pt-6 pb-8 gap-4 static md:absolute w-[90%] md:w-[400px] mx-auto md:mx-0 z-30 md:mt-0"
  >
    <p class="flex justify-center items-center gap-2 text-ROJO_LEON font-semibold text-base md:text-lg text-center">
      <img
        class="h-3 translate-y-[1px]"
        src="/img/shared/point-arrow-iconv2.svg"
        alt="point-arrow-icon"
      />
      <span>
        Busca tu vehículo para la
        <span class="font-extrabold">Gestión de la Revisión Técnica</span>
      </span>
    </p>

    <InputCarPlate
      :value="patente"
      @changePlate="actualizarPatente"
      @plateCompleted="onPatenteCompleta"
    />

    <div class="flex flex-col items-center">
      <p class="flex justify-center items-center gap-2 text-ROJO_LEON font-bold text-base md:text-lg text-center">
        <img
          class="h-3 translate-y-[1px]"
          src="/img/shared/point-arrow-iconv2.svg"
          alt="point-arrow-icon"
        />
        ¿Dónde te encuentras?
      </p>

      <div
        ref="contenedor_input_ref"
        class="mt-3 relative w-full"
      >
        <div class="flex w-full items-center gap-3 rounded-xl bg-white px-4 py-3">
          <img
            src="/img/dc_img/location_on.svg"
            alt="Ubicación"
            class="h-6 w-6"
          />

          <input
            ref="input_ubicacion_ref"
            v-model="ubicacion_texto"
            type="text"
            autocomplete="off"
            spellcheck="false"
            placeholder="Ingresa tu ubicación"
            class="w-full border-none bg-transparent text-base text-GRIS_LEON outline-none placeholder:text-gray-400"
            @input="onInputUbicacion"
            @focus="onFocusUbicacion"
            @keydown="manejarTeclado"
          />
        </div>

        <div
          v-show="mostrar_sugerencias && sugerencias_ui.length"
          ref="contenedor_sugerencias_ref"
          class="pac-container-custom"
        >
          <div
            v-for="(sugerencia_ui, index) in sugerencias_ui"
            :key="sugerencia_ui.id"
            class="pac-item-custom"
            :class="{ 'pac-item-selected': indice_sugerencia_activa === index }"
            @mousedown.prevent="seleccionarSugerencia(sugerencia_ui)"
            @mouseenter="indice_sugerencia_activa = index"
          >
            <span class="pac-item-icon">📍</span>

            <div class="pac-item-text">
              <div class="pac-item-main">
                {{ sugerencia_ui.texto_principal }}
              </div>
              <div class="pac-item-secondary">
                {{ sugerencia_ui.texto_secundario }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <p
        v-if="cargando_ubicacion"
        class="mt-3 text-sm font-medium text-GRIS_LEON text-center"
      >
        Obteniendo tu ubicación actual...
      </p>

      <p
        v-if="mensaje_error"
        class="mt-3 text-sm font-semibold text-ROJO_LEON text-center"
      >
        {{ mensaje_error }}
      </p>

      <button
        type="button"
        :disabled="buscando"
        class="mt-5 w-[68%] max-w-[280px] h-[48px] rounded-[12px] flex items-center justify-center gap-2 font-bold text-[15px] transition bg-ROJO_LEON text-white disabled:opacity-60 disabled:cursor-not-allowed"
        @click="buscar"
      >
        {{ buscando ? 'Buscando...' : 'Buscar' }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { markRaw, nextTick, onMounted, onUnmounted, ref } from 'vue'
import { useStore } from 'vuex'
import InputCarPlate from '~/components/Inputs/InputCarPlate.vue'
import useGoogleServices from '~/composables/useGoogleServices'

const router = useRouter()
const store = useStore()
const { cargarPlaces, geocodificar, crearBuscadorLugares } = useGoogleServices('FormularioRevisionTecnica')
const { buscarSugerencias, obtenerLugar } = crearBuscadorLugares({ regiones: ['CL'] })

const patente = ref('')
const ubicacion_texto = ref('')
const cargando_ubicacion = ref(false)
const buscando = ref(false)
const mensaje_error = ref('')

const input_ubicacion_ref = ref(null)
const contenedor_input_ref = ref(null)
const contenedor_sugerencias_ref = ref(null)

const sugerencias_ui = ref([])
const mostrar_sugerencias = ref(false)
const indice_sugerencia_activa = ref(-1)

let debounce_timer = null
let google_maps_cargado = false

const obtener_search_data = () => {
  return store.getters['revisiontecnica/getSearchData'] || {}
}

const obtener_componente = (componentes, tipo) => {
  return (componentes || []).find((item) => item.types?.includes(tipo))?.long_name || ''
}

const normalizar_place_desde_geocoder = (place) => {
  const latitud = typeof place.geometry?.location?.lat === 'function'
    ? place.geometry.location.lat()
    : place.geometry?.location?.lat ?? null

  const longitud = typeof place.geometry?.location?.lng === 'function'
    ? place.geometry.location.lng()
    : place.geometry?.location?.lng ?? null

  const componentes = place.address_components || []

  const calle_nombre = obtener_componente(componentes, 'route')
  const calle_numero = obtener_componente(componentes, 'street_number')
  const region = obtener_componente(componentes, 'administrative_area_level_1')
  const comuna =
    obtener_componente(componentes, 'administrative_area_level_3') ||
    obtener_componente(componentes, 'locality') ||
    obtener_componente(componentes, 'administrative_area_level_2')

  return {
    ubicacion: place.formatted_address || ubicacion_texto.value || '',
    latitud,
    longitud,
    calle_nombre,
    calle_numero,
    region,
    comuna,
  }
}

const normalizar_place_desde_place_api = (address_components, location, texto_direccion) => {
  const calle_nombre = obtener_componente(address_components, 'route')
  const calle_numero = obtener_componente(address_components, 'street_number')
  const region = obtener_componente(address_components, 'administrative_area_level_1')
  const comuna =
    obtener_componente(address_components, 'administrative_area_level_3') ||
    obtener_componente(address_components, 'locality') ||
    obtener_componente(address_components, 'administrative_area_level_2')

  return {
    ubicacion: texto_direccion || ubicacion_texto.value || '',
    latitud: typeof location?.lat === 'function' ? location.lat() : null,
    longitud: typeof location?.lng === 'function' ? location.lng() : null,
    calle_nombre,
    calle_numero,
    region,
    comuna,
  }
}

const guardar_busqueda = (datos) => {
  store.commit('revisiontecnica/SET_SEARCH_DATA', {
    modo_busqueda: 'por_patente',
    patente: patente.value,
    local: '',
    local_id: null,
    marca: '',
    ano: '',
    modelo: '',
    version: '',
    ubicacion: datos.ubicacion || '',
    latitud: datos.latitud ?? null,
    longitud: datos.longitud ?? null,
    calle_nombre: datos.calle_nombre || '',
    calle_numero: datos.calle_numero || '',
    region: datos.region || '',
    comuna: datos.comuna || '',
  })
}

const limpiar_ubicacion_confirmada = () => {
  store.commit('revisiontecnica/SET_SEARCH_DATA', {
    local: '',
    local_id: null,
    ubicacion: '',
    latitud: null,
    longitud: null,
    calle_nombre: '',
    calle_numero: '',
    region: '',
    comuna: '',
  })
}

const actualizarPatente = (valor) => {
  patente.value = (valor || '').toUpperCase()
}

const onPatenteCompleta = () => {
  patente.value = (patente.value || '').toUpperCase()
}

const limpiar_sugerencias = () => {
  sugerencias_ui.value = []
  mostrar_sugerencias.value = false
  indice_sugerencia_activa.value = -1
}

const asegurar_google_maps = async () => {
  if (google_maps_cargado) return

  await cargarPlaces()
  google_maps_cargado = true
}

const geocodificar_texto = async (direccion) => {
  try {
    const results = await geocodificar({
      address: direccion,
      componentRestrictions: { country: 'CL' },
    })
    return results[0]
  } catch (error) {
    throw new Error('No fue posible resolver la ubicación ingresada.')
  }
}

const geocodificar_coordenadas = async (latitud, longitud) => {
  try {
    const results = await geocodificar({
      location: { lat: latitud, lng: longitud },
    })
    return results[0]
  } catch (error) {
    throw new Error('No fue posible obtener la dirección actual.')
  }
}

const normalizar_sugerencias_ui = (resultado = []) => {
  return (resultado || []).map((sugerencia, index) => ({
    id: `rt-${Date.now()}-${index}`,
    texto_principal: sugerencia?.placePrediction?.text?.text || '',
    texto_secundario: sugerencia?.placePrediction?.structuredFormat?.secondaryText?.text || '',
    raw: markRaw(sugerencia),
  }))
}

const obtener_predicciones = async (texto) => {
  const suggestions = await buscarSugerencias(texto)
  return normalizar_sugerencias_ui(suggestions)
}

const onInputUbicacion = () => {
  mensaje_error.value = ''
  indice_sugerencia_activa.value = -1

  const valor = (ubicacion_texto.value || '').trim()
  const search_data = obtener_search_data()
  const ubicacion_confirmada = (search_data.ubicacion || '').trim()

  if (valor !== ubicacion_confirmada) {
    limpiar_ubicacion_confirmada()
  }

  if (!valor || valor.length < 4) {
    limpiar_sugerencias()
    return
  }

  clearTimeout(debounce_timer)

  debounce_timer = setTimeout(async () => {
    try {
      await asegurar_google_maps()
      const resultado = await obtener_predicciones(valor)
      sugerencias_ui.value = resultado
      mostrar_sugerencias.value = resultado.length > 0
    } catch (error) {
      console.error('[FormularioRevisionTecnica] Error en input ubicación:', error)
      limpiar_sugerencias()
    }
  }, 300)
}

const onFocusUbicacion = async () => {
  const valor = (ubicacion_texto.value || '').trim()

  if (valor.length < 4) return

  try {
    await asegurar_google_maps()
    const resultado = await obtener_predicciones(valor)
    sugerencias_ui.value = resultado
    mostrar_sugerencias.value = resultado.length > 0
  } catch (error) {
    console.error('[FormularioRevisionTecnica] Error al enfocar ubicación:', error)
  }
}

const seleccionarSugerencia = async (sugerencia_ui) => {
  try {
    await asegurar_google_maps()

    const sugerencia_raw = sugerencia_ui?.raw
    const texto_direccion = sugerencia_ui?.texto_principal || ''

    if (!sugerencia_raw?.placePrediction) {
      throw new Error('La sugerencia seleccionada no es válida.')
    }

    const { place } = await obtenerLugar(sugerencia_raw)

    const datos = normalizar_place_desde_place_api(
      place.addressComponents || [],
      place.location,
      texto_direccion
    )

    ubicacion_texto.value = datos.ubicacion
    guardar_busqueda(datos)

    limpiar_sugerencias()
  } catch (error) {
    console.error('[FormularioRevisionTecnica] Error al seleccionar sugerencia:', error)
    mensaje_error.value = 'No fue posible seleccionar la ubicación.'
  }
}

const manejarTeclado = async (event) => {
  const hay_sugerencias_visibles = mostrar_sugerencias.value && sugerencias_ui.value.length > 0

  if (event.key === 'ArrowDown') {
    if (!hay_sugerencias_visibles) return
    event.preventDefault()
    indice_sugerencia_activa.value =
      indice_sugerencia_activa.value < sugerencias_ui.value.length - 1
        ? indice_sugerencia_activa.value + 1
        : 0
    return
  }

  if (event.key === 'ArrowUp') {
    if (!hay_sugerencias_visibles) return
    event.preventDefault()
    indice_sugerencia_activa.value =
      indice_sugerencia_activa.value > 0
        ? indice_sugerencia_activa.value - 1
        : sugerencias_ui.value.length - 1
    return
  }

  if (event.key === 'Enter') {
    event.preventDefault()

    if (
      hay_sugerencias_visibles &&
      indice_sugerencia_activa.value >= 0 &&
      sugerencias_ui.value[indice_sugerencia_activa.value]
    ) {
      await seleccionarSugerencia(sugerencias_ui.value[indice_sugerencia_activa.value])
      return
    }

    await buscar()
    return
  }

  if (event.key === 'Escape') {
    limpiar_sugerencias()
  }
}

const manejarClickDocumento = (event) => {
  const contenedor_input = contenedor_input_ref.value
  const contenedor_sugerencias = contenedor_sugerencias_ref.value

  if (
    contenedor_input &&
    contenedor_input.contains(event.target)
  ) {
    return
  }

  if (
    contenedor_sugerencias &&
    contenedor_sugerencias.contains(event.target)
  ) {
    return
  }

  limpiar_sugerencias()
}

const cargar_ubicacion_actual = async () => {
  if (!navigator.geolocation) return

  cargando_ubicacion.value = true

  try {
    const posicion = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        resolve,
        reject,
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      )
    })

    const latitud = posicion.coords.latitude
    const longitud = posicion.coords.longitude
    const resultado = await geocodificar_coordenadas(latitud, longitud)
    const datos = normalizar_place_desde_geocoder(resultado)

    ubicacion_texto.value = datos.ubicacion
    guardar_busqueda(datos)
  } catch (error) {
    console.warn('[FormularioRevisionTecnica] No fue posible obtener ubicación actual:', error)
  } finally {
    cargando_ubicacion.value = false
  }
}

const buscar = async () => {
  mensaje_error.value = ''
  buscando.value = true

  try {
    await asegurar_google_maps()

    const patente_limpia = (patente.value || '')
      .replace(/[^A-Za-z0-9]/g, '')
      .toUpperCase()
      .trim()

    const ubicacion_limpia = (ubicacion_texto.value || '').trim()

    if (patente_limpia.length < 6) {
      throw new Error('Debes ingresar una patente válida.')
    }

    if (!ubicacion_limpia) {
      throw new Error('Debes ingresar una ubicación.')
    }

    const search_data = obtener_search_data()

    if (
      !search_data.latitud ||
      !search_data.longitud ||
      search_data.ubicacion !== ubicacion_limpia
    ) {
      const resultado = await geocodificar_texto(ubicacion_limpia)
      const datos = normalizar_place_desde_geocoder(resultado)

      patente.value = patente_limpia
      ubicacion_texto.value = datos.ubicacion
      guardar_busqueda(datos)
    } else {
      store.commit('revisiontecnica/SET_SEARCH_DATA', {
        patente: patente_limpia,
        modo_busqueda: 'por_patente',
        local: '',
        local_id: null,
      })
    }

    store.commit('revisiontecnica/RESET_DETALLE_AGENDAMIENTO')
    store.commit('revisiontecnica/RESET_PERSONAL_INFORMATION')

    limpiar_sugerencias()

    await router.push('/revision-tecnica/servicio')
  } catch (error) {
    mensaje_error.value = error.message || 'No fue posible continuar con la búsqueda.'
  } finally {
    buscando.value = false
  }
}

onMounted(async () => {
  store.commit('revisiontecnica/LOAD_FROM_STORAGE')

  const search_data = obtener_search_data()
  patente.value = (search_data.patente || '').toUpperCase()
  ubicacion_texto.value = search_data.ubicacion || ''

  try {
    await nextTick()
    await asegurar_google_maps()

    document.addEventListener('click', manejarClickDocumento)

    if (!search_data.ubicacion) {
      await cargar_ubicacion_actual()
    }
  } catch (error) {
    console.error('[FormularioRevisionTecnica] error:', error)
    mensaje_error.value = 'No fue posible cargar el buscador de ubicación.'
  }
})

onUnmounted(() => {
  clearTimeout(debounce_timer)
  document.removeEventListener('click', manejarClickDocumento)
})
</script>

<style scoped>
.pac-container-custom {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 60;
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  max-height: 260px;
  overflow-y: auto;
}

.pac-item-custom {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  cursor: pointer;
  border-bottom: 1px solid #f1f1f1;
  background: #fff;
}

.pac-item-custom:last-child {
  border-bottom: 0;
}

.pac-item-custom:hover,
.pac-item-selected {
  background: #f8f8f8;
}

.pac-item-icon {
  flex-shrink: 0;
  font-size: 14px;
  line-height: 1.4;
}

.pac-item-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.pac-item-main {
  font-size: 14px;
  font-weight: 700;
  color: #2f2f2f;
  line-height: 1.3;
}

.pac-item-secondary {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.3;
}
</style>